//////////////////
// IMPORTS
//////////////////
// schema
// import * as ISchema from '@schema';
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
// team-health-dashboard imports
import { IProps, IContainer, IState, IHealthFetchVarsByTeam } from "./TeamHealthDashboard.d";
import * as s from "./TeamHealthDashboard.styles";
// other components
import { TeammateAnalyticsCard } from "@components/teammate-analytics-card";
import { CalloutBox } from "@components/callout-box";
import { HealthPeriodPicker } from "@components/health-period-picker";
import { TeamPickerWithActions } from "@components/team-picker-with-actions";
import { CircularProgress } from "@clockwise/web-commons/src/components/circular-progress";
// state
import { IReduxState } from "@reducers/root.reducer";
import { setSettingsDialogOpen } from "@actions/chrome-extension.actions";
import { openTeam } from "@actions/settings.actions";
// util
import { maybeGetPrimaryTeamFromOrg, isUserTeamsSame } from "@util/team.util";
import { logger } from "@util/logger.util";
import {
  getCollatedPersonsAndScoresOffOrg,
  IScoredPerson,
  GOAL_MINUTES,
} from "@util/team-health.util";
import { page, PageEvents, track, TrackingEvents } from "@util/analytics.util";
import { copyTextToClipboard } from "@util/html.util";
import { personalInviteBaseUrl } from "@constants/site.constant";
import { fromGlobalId } from "@util/graphql.util";
// material-ui
import { Button } from "@material-ui/core";
import { Add, Link } from "@material-ui/icons";
import { TeamHealthDashboardAnim } from "./TeamHealthDashboardAnim";

//////////////////
// COMPONENT
//////////////////
class TeamHealthDashboardCmpt extends React.Component<IProps, IState> {
  private cachedFetchVars: IHealthFetchVarsByTeam; // saved for refetching
  // ~-~-~-~-~-~-~-
  // Setup
  // ~-~-~-~-~-~-~-
  constructor(props: IProps) {
    super(props);

    const maybePrimaryTeam = maybeGetPrimaryTeamFromOrg(props.org);
    const primaryTeamId = maybePrimaryTeam ? maybePrimaryTeam.teamId : "";
    this.state = {
      loading: false,
      selectedTeamId: primaryTeamId,
      inviteLinkCopied: false,
    };

    this.cachedFetchVars = props.initialFetchVars;
  }

  // ~-~-~-~-~-~-~-
  // Life-Cycle
  // ~-~-~-~-~-~-~-
  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    const isSame = isUserTeamsSame(nextProps.org.userTeams, this.props.org.userTeams);
    const userTeamsList =
      nextProps.org.userTeams.__typename === "TeamList" ? nextProps.org.userTeams.list : null;
    if (this.props.refetchFlag !== nextProps.refetchFlag || !isSame) {
      this.refetch({
        ...this.cachedFetchVars,
        teamId:
          this.state.selectedTeamId ||
          (userTeamsList && userTeamsList.length > 0 && userTeamsList[0].teamId) ||
          "",
        hasTeams: !!userTeamsList && userTeamsList.length > 0,
      });

      // if state's selected team is not in next team list, then set new selected team
      if (
        userTeamsList &&
        userTeamsList.findIndex((v) => v.teamId === this.state.selectedTeamId) === -1
      ) {
        const maybePrimaryTeam = maybeGetPrimaryTeamFromOrg(nextProps.org);
        this.onSelectedTeamChange(maybePrimaryTeam ? maybePrimaryTeam.teamId : "");
      }
    }
  }

  public componentDidMount() {
    page(PageEvents.TEAM_HEALTH_DASHBOARD);
  }

  // ~-~-~-~-~-~-~-
  // Helper
  // ~-~-~-~-~-~-~-
  public getCalendarStatus = (scoredPerson: IScoredPerson) => {
    if (
      !scoredPerson.calendarFocusTimeScore.calendarId ||
      this.props.org.teamCalendarStatusErrorable.__typename === "GraphEntityError"
    ) {
      return null;
    }

    // TODO - pregenerate a map
    const teamStatuses = this.props.org.teamCalendarStatusErrorable;
    const status = teamStatuses.list.find(
      (stat) => stat.calendarId === scoredPerson.calendarFocusTimeScore.calendarId,
    );
    return status || null;
  };

  public getProfileTimeZone = (scoredPerson: IScoredPerson, primaryTeamId?: string) => {
    const { org } = this.props;

    const teamHealthScoreErrorable = primaryTeamId
      ? org.teamHealthScoreForTeamErrorable
      : org.teamHealthScoreJustForUserErrorable;

    if (
      !scoredPerson.calendarFocusTimeScore.calendarId ||
      teamHealthScoreErrorable.__typename === "GraphEntityError" ||
      !teamHealthScoreErrorable.profileTimeZones.length
    ) {
      return null;
    }

    // TODO - pregenerate a map
    const timeZones = teamHealthScoreErrorable.profileTimeZones;
    const timeZone = timeZones.find(
      (tz) => tz.calendarId === scoredPerson.calendarFocusTimeScore.calendarId,
    );
    return timeZone || null;
  };

  private onSelectedTeamChange = (teamId: string) => {
    this.setState({ selectedTeamId: teamId });
    this.refetch({ ...this.cachedFetchVars, teamId, hasTeams: this.cachedFetchVars.hasTeams });
  };

  private openTeamsDialog = (teamId?: string, createOrJoin?: string) => {
    this.props.setSettingsDialogOpen(true);
    this.props.openTeam(teamId, undefined, undefined, createOrJoin);
  };

  private onClickCopyLink = () => {
    const { user } = this.props;
    const userToken = (user.id && fromGlobalId(user.id).id.substr(0, 7)) || "cw";
    const inviteUrl = `${personalInviteBaseUrl}/${userToken}`;
    const success = copyTextToClipboard(inviteUrl, true);
    if (success) {
      this.setState({ inviteLinkCopied: true });
      track(TrackingEvents.TEAM_HEALTH.COPY_INVITE_LINK);
      setTimeout(() => {
        this.setState({ inviteLinkCopied: false });
      }, 2100);
    }
  };
  // ~-~-~-~-~-~-~-
  // Async
  // ~-~-~-~-~-~-~-
  private refetchCallback = (error?: Error) => {
    if (error) {
      logger.error("An error occurred during refetch within TeamHealthDashboard", error);
    }
    this.setState({ loading: false });
  };

  private refetch = (vars: IHealthFetchVarsByTeam, force?: boolean) => {
    // only refetch if chrome extension and open
    if (this.props.isChromeExtension && !this.props.chromeExtensionOpen) {
      return;
    }

    this.cachedFetchVars = vars;
    this.setState({ loading: true });
    this.props.relay.refetch(vars, undefined, this.refetchCallback, { force });
  };

  // ~-~-~-~-~-~-~-
  // Rendering
  // ~-~-~-~-~-~-~-
  private maybeRenderAddTeamsCard = () => {
    const { loading, selectedTeamId } = this.state;
    if (loading) {
      return "";
    }

    // if no teams, show this card
    return (
      <Button
        fullWidth
        cw-id="teammate-list-add-teammate"
        variant="outlined"
        color="primary"
        onClick={() => {
          this.props.setSettingsDialogOpen(true);
          this.props.openTeam(selectedTeamId);
          track(TrackingEvents.TEAMS.TEAMS_TEAM_ANALYTICS_ADD_TEAMMATES_BUTTON_CLICKED);
        }}
        style={s.addTeammatesBtn}
      >
        <Add style={s.btnIcon} />
        <div style={{ flex: 1 }}>Add teammates</div>
      </Button>
    );
  };

  private maybeRenderCopyLinkCard = () => {
    const { loading } = this.state;
    if (loading) {
      return null;
    }
    return !this.state.inviteLinkCopied ? (
      <Button
        fullWidth
        cw-id="teammate-list-add-teammate"
        variant="outlined"
        color="primary"
        onClick={this.onClickCopyLink}
        style={s.addTeammatesBtn}
      >
        <Link style={s.btnIcon} />
        <div style={{ flex: 1 }}>Copy Invite Link</div>
      </Button>
    ) : (
      <Button
        fullWidth
        cw-id="teammate-list-add-teammate"
        onClick={this.onClickCopyLink}
        style={s.copiedInviteLinkBtn}
      >
        <TeamHealthDashboardAnim> Link copied to your clipboard 🙌 </TeamHealthDashboardAnim>
      </Button>
    );
  };

  public render() {
    // check for data requirements
    const { org } = this.props;
    const { loading, refetchError, selectedTeamId } = this.state;
    const userTeams = org.userTeams.__typename === "TeamList" ? org.userTeams : null;
    const maybePrimaryTeam = maybeGetPrimaryTeamFromOrg(org);
    const primaryTeamId = maybePrimaryTeam ? maybePrimaryTeam.teamId : "";

    // catch errors in fetches
    const personFetchError = primaryTeamId
      ? !org ||
        !org.teamHealthScoreForTeamErrorable ||
        org.teamHealthScoreForTeamErrorable.__typename === "GraphEntityError" ||
        !org.teamHealthScoreForTeamErrorable.persons ||
        org.teamHealthScoreForTeamErrorable.persons.__typename === "GraphEntityError"
      : !org ||
        !org.teamHealthScoreJustForUserErrorable ||
        org.teamHealthScoreJustForUserErrorable.__typename === "GraphEntityError" ||
        !org.teamHealthScoreJustForUserErrorable.persons ||
        org.teamHealthScoreJustForUserErrorable.persons.__typename === "GraphEntityError";

    if (!!refetchError || !!personFetchError) {
      return (
        <div style={s.content}>
          <CalloutBox warning style={{ marginTop: 20 }}>
            Sorry, loading Team Analytics failed. Please try again.
          </CalloutBox>
        </div>
      );
    }

    // pairs all the scores/profiles and sorts properly
    const scoredPersons = getCollatedPersonsAndScoresOffOrg(org, undefined, !primaryTeamId);

    return (
      <div style={s.content}>
        <div style={s.dashboard}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              marginBottom: 10,
            }}
          >
            {primaryTeamId && userTeams ? (
              <div style={s.dropdownContainer}>
                <TeamPickerWithActions
                  userTeams={userTeams}
                  teamId={selectedTeamId}
                  onChange={this.onSelectedTeamChange}
                  primaryTeamId={primaryTeamId}
                  disabled={loading}
                  openTeamsDialog={this.openTeamsDialog}
                  disableUnderline
                />
              </div>
            ) : (
              ""
            )}
          </div>
          {/* this component watches the gcal state and has a callback to refetch on change of that so i have to keep this for now and just make it invissible */}
          <div
            style={{
              ...s.dropdownContainer,
              visibility: "hidden",
              height: 0,
              padding: 0,
              overflow: "hidden",
            }}
          >
            <HealthPeriodPicker
              orgId={org.id}
              disabled={loading}
              onChange={(vars) =>
                this.refetch({
                  ...vars,
                  teamId: this.cachedFetchVars.teamId,
                  hasTeams: this.cachedFetchVars.hasTeams,
                })
              }
              label="Time Range"
              style={userTeams && primaryTeamId ? { flex: 1 } : {}}
            />
          </div>
          {loading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
              }}
            >
              <CircularProgress />
            </div>
          ) : (
            ""
          )}
          {!loading &&
            scoredPersons.map((scoredPerson, i) => {
              return (
                <TeammateAnalyticsCard
                  org={org}
                  key={scoredPerson.calendarFocusTimeScore.calendarId}
                  calendarFocusTimeScore={scoredPerson.calendarFocusTimeScore}
                  person={scoredPerson.person}
                  orgPerson={scoredPerson.orgPerson}
                  teamId={selectedTeamId}
                  goalMinutes={GOAL_MINUTES}
                  maxMinutes={scoredPerson.maxMinutes}
                  calendarStatus={this.getCalendarStatus(scoredPerson)}
                  profileTimeZone={this.getProfileTimeZone(scoredPerson, primaryTeamId)}
                  style={i === 0 ? { marginTop: 0 } : {}}
                  currentUserTimeZone={this.getProfileTimeZone(scoredPersons[0], primaryTeamId)}
                />
              );
            })}
          {this.maybeRenderAddTeamsCard()}
          {this.maybeRenderCopyLinkCard()}
        </div>
      </div>
    );
  }
}

//////////////////
// REDUX
//////////////////
const TeamHealthDashboardRedux = connect(
  (state: IReduxState, _ownProps: IContainer) => ({
    isChromeExtension: state.webExtension.isWebExtension,
    chromeExtensionOpen: state.webExtension.webExtensionOpen,
    refetchFlag: state.teamHealth.refetchTeamHealth,
  }),
  {
    setSettingsDialogOpen,
    openTeam,
  },
)(TeamHealthDashboardCmpt);

//////////////////
// RELAY
//////////////////
import { fragments, query } from "./TeamHealthDashboard.gql";
export const TeamHealthDashboard = createRefetchContainer<IContainer>(
  TeamHealthDashboardRedux,
  fragments,
  query,
);
